import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE, fmtCHF, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Lock, Loader2 } from "lucide-react";

const quarter = () => {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  const y = q === 0 ? d.getFullYear() - 1 : d.getFullYear();
  const qi = q === 0 ? 3 : q - 1;
  const s = new Date(Date.UTC(y, qi * 3, 1)), e = new Date(Date.UTC(y, qi * 3 + 3, 0));
  return [s.toISOString().slice(0, 10), e.toISOString().slice(0, 10)];
};

const FILES = [
  { kind: "vat.pdf", label: tx("ui_fiduciaryview.k8e5a9e"), icon: FileText },
  { kind: "entries.xlsx", label: tx("ui_fiduciaryview.k49af41"), icon: FileSpreadsheet },
  { kind: "entries.csv", label: tx("ui_fiduciaryview.ka2d8e3"), icon: FileSpreadsheet },
  { kind: "sales.pdf", label: tx("ui_fiduciaryview.k84ee42"), icon: FileText },
  { kind: "invoices.pdf", label: tx("ui_fiduciaryview.k962b6f"), icon: FileText },
];

export default function FiduciaryView() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [[start, end], setRange] = useState(quarter());
  const [month, setMonth] = useState(new Date(Date.now() - 20 * 864e5).toISOString().slice(0, 7));
  const [vat, setVat] = useState(null);
  const base = `${API_BASE}/fid/${token}`;

  useEffect(() => {
    fetch(base).then((r) => (r.ok ? r.json() : Promise.reject())).then(setInfo).catch(() => setError(true));
  }, [base]);

  useEffect(() => {
    if (!info || !start || !end) return;
    fetch(`${base}/vat?start=${start}&end=${end}`).then((r) => r.json()).then(setVat).catch(() => setVat(null));
  }, [info, base, start, end]);

  const dl = (kind, extra) => downloadFile(`${base}/file/${kind}?${extra}`, `oresto-${kind}`).catch(() => toast.error(tx("ui_fiduciaryview.kf91b43")));

  if (error) return <div className="min-h-screen bg-bg flex items-center justify-center p-6"><p className="card-c text-sm" data-testid="fid-invalid">{tx("ui_fiduciaryview.kae191d")}</p></div>;
  if (!info) return <div className="min-h-screen bg-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;

  return (
    <div className="min-h-screen bg-bg p-4 sm:p-8">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="fiduciary-view">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-c bg-primary-c/10 px-3 py-1 rounded-full"><Lock className="w-3.5 h-3.5" /> {tx("ui_fiduciaryview.k370390")}</p>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl" data-testid="fid-restaurant">{info.restaurant || "Restaurant"}</h1>
          <p className="text-sm text-text-secondary mt-1">{[info.address, info.canton].filter(Boolean).join(" · ")}</p>
          <p className="text-sm text-text-secondary">{tx("tpl.vat_no")} {info.vat_number || tx("ui_fiduciaryview.kcd00f1")} · {tx("tpl.method")} <strong>{info.vat_method === "tdfn" ? tx("onb.m_tdfn") : tx("onb.m_effective")}</strong></p>
          <p className="text-xs text-text-secondary mt-1">Responsable : {info.owner} ({info.owner_email})</p>
        </div>

        <div className="card-c">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{tx("ui_fiduciaryview.k756d30")}</p>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={start} onChange={(e) => setRange([e.target.value, end])} className="input-c text-sm" data-testid="fid-start" />
            <input type="date" value={end} onChange={(e) => setRange([start, e.target.value])} className="input-c text-sm" data-testid="fid-end" />
          </div>
          {vat && (
            <div className="grid grid-cols-3 gap-2 mt-4 text-center" data-testid="fid-vat-summary">
              <div className="rounded-xl bg-surface-muted p-3"><p className="text-[10px] uppercase text-text-secondary">{tx("ui_fiduciaryview.ka68be3")}</p><p className="font-semibold text-sm mt-1">{fmtCHF(vat.ca_total_ttc)}</p></div>
              <div className="rounded-xl bg-surface-muted p-3"><p className="text-[10px] uppercase text-text-secondary">{vat.vat_method === "tdfn" ? tx("ui_fiduciaryview.kdda758") : tx("ui_fiduciaryview.kb4809b")}</p><p className="font-semibold text-sm mt-1">{fmtCHF(vat.tva_collectee_total)}</p></div>
              <div className="rounded-xl bg-primary-c/10 p-3"><p className="text-[10px] uppercase text-text-secondary">{tx("ui_fiduciaryview.k55b7ca")}</p><p className="font-semibold text-sm mt-1 text-primary-c" data-testid="fid-vat-net">{fmtCHF(vat.tva_nette_a_payer)}</p></div>
            </div>
          )}
          <div className="grid gap-2 mt-4">
            {FILES.map((f) => (
              <button key={f.kind} onClick={() => dl(f.kind, `start=${start}&end=${end}`)} data-testid={`fid-dl-${f.kind.replace(".", "-")}`} className="flex items-center gap-3 p-3 bg-surface-muted rounded-xl text-left text-sm hover:bg-[#ece8da] transition-colors">
                <f.icon className="w-5 h-5 text-primary-c shrink-0" /> <span className="flex-1">{f.label}</span> <span className="text-xs text-primary-c font-semibold">{tx("ui_fiduciaryview.k294cb2")}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="card-c">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{tx("ui_fiduciaryview.k236b32")}</p>
          <div className="flex gap-2">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-c text-sm flex-1" data-testid="fid-month" />
            <button onClick={() => dl("monthly.pdf", `month=${month}`)} className="btn-primary !py-2 text-sm" data-testid="fid-dl-monthly">PDF</button>
          </div>
        </div>
        <p className="text-center text-xs text-text-secondary">{tx("ui_fiduciaryview.k2c47ca")}</p>
      </div>
    </div>
  );
}
